import { useConsent, openCookiePreferences } from "../lib/consent";
import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ExternalLink } from "lucide-react";

/* Privacy-enhanced YouTube embed (youtube-nocookie.com), per the embed playbook:
   autoplay on open, related videos off, mounted only while the lightbox is open */
const embedUrl = (videoId) => {
  const params = new URLSearchParams({
    autoplay: "1",
    playsinline: "1",
    rel: "0",
    origin: window.location.origin,
  });
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
};

const VideoLightbox = ({ video, onClose }) => {
  const consent = useConsent();
  useEffect(() => {
    if (!video) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const lenis = window.__lenis;
    if (lenis) lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (lenis) lenis.start();
    };
  }, [video, onClose]);

  return (
    <AnimatePresence>
      {video && (
        <motion.div
          data-testid="video-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close video"
              data-testid="video-lightbox-close"
              className="absolute -top-12 right-0 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
            >
              Close <X size={18} />
            </button>
            <div className="overflow-hidden rounded-lg border border-white/10 bg-black shadow-2xl">
              {consent?.media ? <iframe
                key={video.id}
                data-testid="video-lightbox-player"
                src={embedUrl(video.id)}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="w-full aspect-video"
              /> : <div className="aspect-video flex flex-col items-center justify-center gap-4 px-6 text-center text-white"><p className="font-display text-xl">Your Video Preferences</p><p className="max-w-md text-sm text-white/70">Enable embedded videos to watch here, or open this video directly on YouTube.</p><button onClick={() => { onClose(); openCookiePreferences(); }} className="rounded-[6px] bg-[#cf0a0a] px-5 py-3 text-sm font-bold">Manage Preferences</button><a href={`https://www.youtube.com/watch?v=${video.id}`} target="_blank" rel="noreferrer" className="text-sm underline">Watch on YouTube</a></div>}
            </div>
            <div className="mt-4 flex items-start justify-between gap-6">
              <div className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rotate-45 bg-[#cf0a0a] shrink-0" />
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/50">
                    The Standard TV
                  </p>
                  <h3
                    data-testid="video-lightbox-title"
                    className="mt-1 font-display text-lg sm:text-xl font-medium text-white leading-snug"
                  >
                    {video.title}
                  </h3>
                </div>
              </div>
              <a
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noreferrer"
                data-testid="video-lightbox-youtube-link"
                className="mt-1 hidden sm:inline-flex items-center gap-1.5 shrink-0 text-[10px] font-bold uppercase tracking-[0.18em] text-white/50 hover:text-white transition-colors"
              >
                Watch on YouTube <ExternalLink size={11} />
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoLightbox;
